import { firstValueFrom, Observable } from 'rxjs'
import { Component, inject, input, viewChild, signal, computed } from '@angular/core'
import { FileInputComponent } from '../file-input/file-input.component'
import { ToastService } from '../toasts/toasts.service'
import { UploadResponse } from '../../shared/services/expected-results.service'

type UploadFn = (file: File) => Observable<UploadResponse>

@Component({
  selector: 'app-erd-file-uploader',
  imports: [FileInputComponent],
  template: `
    <h4>{{ name() }}</h4>
    <div class="input-container">
      <div
          #fileInput
          app-file-input
          class='pe-2'
          accept='text/csv'
          [disabled]="uploadInProgress()"
          (changeFile)="onSelectFile($event)"
      >
        Upload {{ name() }} File
      </div>
      <button
          class="action-btn primary"
          type="button"
          [disabled]="!uploadEnabled()"
          (click)="onUpload()"
      >
        Upload
      </button>
    </div>
  `,
  styles: `
    .input-container {
      display: flex;
    }
  `,
})
export class ErdFileUploaderComponent {
  private readonly toast = inject(ToastService)

  readonly name = input.required<string>()
  readonly validationRegExp = input.required<RegExp>()
  readonly upload = input.required<UploadFn>()

  protected readonly file = signal<File | undefined>(undefined)
  protected readonly uploadInProgress = signal(false)
  protected readonly fileInputEl = viewChild<FileInputComponent>('fileInput')

  protected readonly uploadEnabled = computed((): boolean =>
    Boolean(this.file() && !this.uploadInProgress()),
  )

  private async validate(file: File): Promise<string | undefined> {
    const text = await file.text()
    if (this.validationRegExp().test(text)) {
      return undefined
    } else {
      return `${this.name()}: file ${file.name} does not match expected format`
    }
  }

  private clearFile() {
    this.fileInputEl()?.clear()
  }

  async onSelectFile(file: File | undefined) {
    this.file.set(undefined)
    if (file) {
      this.uploadInProgress.set(true)
      const message = await this.validate(file)
      this.uploadInProgress.set(false)
      if (message) {
        this.toast.show({ type: 'error', message })
        this.clearFile()
      } else {
        this.file.set(file)
      }
    }
  }

  async onUpload() {
    const file = this.file()
    if (file) {
      this.uploadInProgress.set(true)
      try {
        const upload = this.upload()
        const { message } = await firstValueFrom(upload(file))
        this.toast.show({ type: 'success', message, delayMultiplier: 3 })
        this.file.set(undefined)
        this.clearFile()
      } catch (e) {
        this.file.set(file)
        this.toast.showError(e)
      } finally {
        this.uploadInProgress.set(false)
      }
    }
  }
}